import { type FC, type PropsWithChildren, useMemo, useState } from 'react';
import { getText } from 'i18n/intl';
import { selectObjectTypesMap } from 'slices/objectTypes.slice';
import { useAppSelector } from 'slices/store';
import type { TObjectJob } from 'types/objectJob.type';
import type { TType } from '@timeedit/types';
import {
  Button,
  type ButtonProps,
  Dropdown,
} from '@timeedit/ui-components/antd';
import { JobObjectSelection } from '../hooks/useJobSubmit';
import BulkEditAssociationsModal from './bulkEdit/BulkEditAssociationsModal';
import BulkEditFieldModal from './bulkEdit/BulkEditFieldModal';
import BulkEditGeneralInfoModal from './bulkEdit/BulkEditGeneralInfoModal';
import BulkEditOrganizationModal from './bulkEdit/BulkEditOrganizationModal';

type Props = {
  objectType: TType;
  selectedObjectsCount: number;
  jobObjectSelection: JobObjectSelection;
  handleBulkEditJobSubmitted?: () => void;
  disabled?: boolean;
  type?: ButtonProps['type'];
  size?: ButtonProps['size'];
};

const BulkEditStartJobButton: FC<PropsWithChildren<Props>> = (props) => {
  const [bulkEditModalOpen, setBulkEditModalOpen] = useState<
    'common' | 'fields' | 'organizations' | 'associations' | false
  >(false);

  const bulkEditItems = useMemo(
    () => [
      {
        label: getText('object_manager.bulk-edit-dropdown.general.label'),
        key: 'generalInfo',
        onClick: () => setBulkEditModalOpen('common'),
      },
      {
        label: getText('object_manager.bulk-edit-dropdown.fields.label'),
        key: 'fields',
        onClick: () => setBulkEditModalOpen('fields'),
      },
      {
        label: getText('object_manager.bulk-edit-dropdown.associations.label'),
        key: 'associations',
        onClick: () => setBulkEditModalOpen('associations'),
      },
      {
        label: getText('object_manager.bulk-edit-dropdown.organizations.label'),
        key: 'organizations',
        onClick: () => setBulkEditModalOpen('organizations'),
      },
    ],
    []
  );

  return (
    <>
      <Dropdown
        menu={{
          items: bulkEditItems,
        }}
        trigger={['click']}
        placement="top"
        disabled={props.disabled}
      >
        <Button type={props.type} size={props.size} disabled={props.disabled}>
          {props.children}
        </Button>
      </Dropdown>
      <BulkEditFieldModal
        objectType={props.objectType}
        selectedObjectsCount={props.selectedObjectsCount}
        jobObjectSelection={props.jobObjectSelection}
        setOpen={setBulkEditModalOpen}
        open={bulkEditModalOpen === 'fields'}
        onSubmitSuccess={props.handleBulkEditJobSubmitted}
      />
      <BulkEditGeneralInfoModal
        objectType={props.objectType}
        selectedObjectsCount={props.selectedObjectsCount}
        setOpen={setBulkEditModalOpen}
        open={bulkEditModalOpen === 'common'}
        jobObjectSelection={props.jobObjectSelection}
        onSubmitSuccess={props.handleBulkEditJobSubmitted}
      />
      <BulkEditAssociationsModal
        objectType={props.objectType}
        setOpen={setBulkEditModalOpen}
        open={bulkEditModalOpen === 'associations'}
        onSubmitSuccess={props.handleBulkEditJobSubmitted}
        selectedObjectsCount={props.selectedObjectsCount}
        jobObjectSelection={props.jobObjectSelection}
      />
      <BulkEditOrganizationModal
        objectType={props.objectType}
        selectedObjectsCount={props.selectedObjectsCount}
        setOpen={setBulkEditModalOpen}
        open={bulkEditModalOpen === 'organizations'}
        jobObjectSelection={props.jobObjectSelection}
        onSubmitSuccess={props.handleBulkEditJobSubmitted}
      />
    </>
  );
};

export default BulkEditStartJobButton;

export const useJobBulkEditProps = (job: TObjectJob) => {
  const objectTypesMap = useAppSelector(selectObjectTypesMap);

  const jobObjectSelection = useMemo(() => ({ jobId: job._id }), [job]);

  const objectType = useMemo(
    () => objectTypesMap[job.objectTypeId],
    [job.objectTypeId, objectTypesMap]
  );

  const disabled =
    objectType == null || !['FINISHED', 'FAILED'].includes(job.status);

  return useMemo(
    () => ({
      disabled,
      jobObjectSelection,
      objectType,
      selectedObjectsCount: job.objectsToProcess.length,
      jobSummary: job.summary,
    }),
    [
      disabled,
      jobObjectSelection,
      objectType,
      job.objectsToProcess.length,
      job.summary,
    ]
  );
};
